/**
 * Integrations service — MOCK-FIRST (CT-4443).
 *
 * No backend endpoint exists yet: CT-3904 is in the backlog with no contract.
 * This follows the canonical agentic-studio convention (see
 * `aims-os-agentic-studio/src/services/knowledgePacksService.js`): a
 * mock-driven service with a module-level mutable seed, the platform envelope
 * (`{ status_code, body, meta }`), snake_case request/response bodies, and a
 * `TODO(CT-3904 / backend)` at each real-endpoint call site. Hooks call these
 * functions exactly as they would call the real ones, so the swap is one file.
 *
 * Status is PRE-COMPUTED server-side (mocked here on each entity's `status`
 * field); the frontend only renders it — no client-side derivation. The
 * derivation rules are the open engineering definition on ARP-792.
 *
 * The seed reproduces the prototype figures on `#/integrations`:
 * 13 active · 16 instances · 9 categories · 8 needing attention.
 */
import { aggregateProviderStatus } from '../domains/integrations/utils/aggregateProviderStatus.js';
import { envelope, matchesFilters } from '../shared/utils/mockServiceHelpers.js';

/**
 * Builds an instances array whose aggregate reproduces the declared status
 * (CT-4197): a `working` provider is all-working; an attention provider has one
 * instance in the worst state plus working ones. The service — never a
 * component — owns aggregation.
 */
const buildInstancesForStatus = (status, count) => {
  const n = count ?? 1;
  // 0 instances → empty array so aggregateProviderStatus yields `not_connected`
  // (CT-4197 rule #5), instead of masking it with a synthesized working instance.
  if (n <= 0) return [];
  if (status === 'working') {
    return Array.from({ length: n }, (_, i) => ({ id: `inst-${i + 1}`, status: 'working' }));
  }
  return Array.from({ length: n }, (_, i) => ({
    id: `inst-${i + 1}`,
    status: i === 0 ? status : 'working',
  }));
};

/** Active-capability set derived from the provider category (mock; CT-4161). */
const CAPABILITY_DATA_SYNC_CATEGORIES = new Set([
  'Storage',
  'Data & Analytics',
  'CRM & Sales',
]);
const CAPABILITY_MCP_CATEGORIES = new Set([
  'Communication',
  'Productivity & Docs',
  'Dev & Code',
]);

const buildCapabilities = (category) => [
  { type: 'tool', active: true },
  { type: 'data_sync', active: CAPABILITY_DATA_SYNC_CATEGORIES.has(category) },
  { type: 'mcp', active: CAPABILITY_MCP_CATEGORIES.has(category) },
];

/** Attaches instances, aggregate status/breakdown, and capabilities to a raw seed entry. */
const withAggregate = (entry) => {
  const instances = buildInstancesForStatus(entry.status, entry.instances_count);
  const { status, working_count, needs_attention_count } = aggregateProviderStatus(instances);
  return {
    ...entry,
    instances,
    status,
    working_count,
    needs_attention_count,
    capabilities: buildCapabilities(entry.category),
  };
};

/**
 * Provider slugs that ship a bundled SVG logo; the rest fall back to initials.
 *
 * `status_summary` (CT-4199) is the per-provider tooltip detail on the Already
 * Integrated card. It is a BACKEND-provided, backend-localized string (the real
 * endpoint returns it per Accept-Language); the mock uses English placeholders.
 * When absent, the card falls back to the localized `integrations.attention.summary.*`.
 */
const buildRawSeedIntegrations = () => [
  // ── Needs attention (8) ────────────────────────────────────────────────
  {
    id: 'zendesk',
    slug: 'zendesk',
    name: 'Zendesk',
    category: 'Ticketing & ITSM',
    status: 'auth_expired',
    status_summary: 'Production credentials expired 2 days ago — reauthenticate to restore access.',
    connected: true,
    instances_count: 1,
    calls_per_day: 0,
    used_in_workflows: 0,
    connected_by: { name: 'Danilo Petrov', initials: 'DP' },
    source: 'verified',
    verified: true,
  },
  {
    id: 'google-drive',
    slug: 'google-drive',
    name: 'Google Drive',
    category: 'Storage',
    status: 'auth_expired',
    status_summary: 'OAuth token expired — reconnect to resume file syncing.',
    connected: true,
    instances_count: 2,
    calls_per_day: 1820,
    used_in_workflows: 0,
    connected_by: { name: 'Danilo Petrov', initials: 'DP' },
    source: 'verified',
    verified: true,
  },
  {
    id: 'notion',
    slug: 'notion',
    name: 'Notion',
    category: 'Productivity & Docs',
    status: 'schema_drift',
    status_summary: 'Source schema changed — remap 3 fields to continue.',
    connected: true,
    instances_count: 1,
    calls_per_day: 246,
    used_in_workflows: 1,
    connected_by: { name: 'Danilo Petrov', initials: 'DP' },
    source: 'verified',
    verified: true,
  },
  {
    id: 'salesforce',
    slug: 'salesforce',
    name: 'Salesforce',
    category: 'CRM & Sales',
    status: 'schema_drift',
    status_summary: 'Object schema drifted — 2 workflows paused until remapped.',
    connected: true,
    instances_count: 2,
    calls_per_day: 39,
    used_in_workflows: 4,
    connected_by: { name: 'Maria Gomez', initials: 'MG' },
    source: 'verified',
    verified: true,
  },
  {
    id: 'datadog',
    slug: 'datadog',
    name: 'Datadog',
    category: 'Security & Observability',
    status: 'schema_drift',
    status_summary: 'Metric schema changed — dashboards may be incomplete until resolved.',
    connected: true,
    instances_count: 1,
    calls_per_day: 3,
    used_in_workflows: 2,
    connected_by: { name: 'Maria Gomez', initials: 'MG' },
    source: 'verified',
    verified: true,
  },
  {
    id: 'jira',
    slug: 'jira',
    name: 'Jira',
    category: 'Ticketing & ITSM',
    status: 'needs_mapping',
    status_summary: '5 fields need mapping before this integration can run.',
    connected: true,
    instances_count: 1,
    calls_per_day: 128,
    used_in_workflows: 1,
    connected_by: { name: 'Danilo Petrov', initials: 'DP' },
    source: 'verified',
    verified: true,
  },
  {
    id: 'amazon-s3',
    slug: 'amazon-s3',
    name: 'Amazon S3',
    category: 'Storage',
    status: 'needs_mapping',
    status_summary: 'Bucket fields unmapped — mapping required to enable sync.',
    connected: true,
    instances_count: 2,
    calls_per_day: 540,
    used_in_workflows: 3,
    connected_by: { name: 'Maria Gomez', initials: 'MG' },
    source: 'verified',
    verified: true,
  },
  {
    id: 'internal-billing-api',
    slug: 'internal-billing-api',
    name: 'Internal Billing API',
    category: 'Custom / Build your own',
    status: 'needs_mapping',
    status_summary: 'Response fields need mapping before first use.',
    connected: true,
    instances_count: 1,
    calls_per_day: 92,
    used_in_workflows: 2,
    connected_by: { name: 'Maria Gomez', initials: 'MG' },
    source: 'workspace',
    verified: false,
  },
  // ── Working (5 more → 13 active total) ─────────────────────────────────
  {
    id: 'slack',
    slug: 'slack',
    name: 'Slack',
    category: 'Communication',
    status: 'working',
    connected: true,
    instances_count: 1,
    calls_per_day: 4210,
    used_in_workflows: 6,
    connected_by: { name: 'Danilo Petrov', initials: 'DP' },
    source: 'verified',
    verified: true,
  },
  {
    id: 'github',
    slug: 'github',
    name: 'GitHub',
    category: 'Dev & Code',
    status: 'working',
    connected: true,
    instances_count: 1,
    calls_per_day: 980,
    used_in_workflows: 3,
    connected_by: { name: 'Maria Gomez', initials: 'MG' },
    source: 'verified',
    verified: true,
  },
  {
    id: 'snowflake',
    slug: 'snowflake',
    name: 'Snowflake',
    category: 'Data & Analytics',
    status: 'working',
    connected: true,
    instances_count: 1,
    calls_per_day: 315,
    used_in_workflows: 2,
    connected_by: { name: 'Maria Gomez', initials: 'MG' },
    source: 'verified',
    verified: true,
  },
  {
    id: 'linear',
    slug: 'linear',
    name: 'Linear',
    category: 'Productivity & Docs',
    status: 'working',
    connected: true,
    instances_count: 1,
    calls_per_day: 74,
    used_in_workflows: 1,
    connected_by: { name: 'Danilo Petrov', initials: 'DP' },
    source: 'verified',
    verified: true,
  },
  {
    id: 'spreadsheet',
    slug: 'spreadsheet',
    name: 'Spreadsheet',
    category: 'Custom / Build your own',
    status: 'working',
    connected: true,
    instances_count: 1,
    calls_per_day: 12,
    used_in_workflows: 0,
    connected_by: { name: 'AIMS-OS native', initials: 'S' },
    source: 'native',
    verified: false,
  },
];

/** Seed with each provider's aggregate status derived from its instances (CT-4197). */
const buildSeedIntegrations = () => buildRawSeedIntegrations().map(withAggregate);

/** Module-level mutable seed so create/update reflect in list/get within a process. */
let mockIntegrations = buildSeedIntegrations();

/** Test-only: reset the mutable seed between tests within a file. */
export const __resetMockIntegrationsForTests = () => {
  mockIntegrations = buildSeedIntegrations();
};

const CATEGORY_ORDER = [
  'Ticketing & ITSM',
  'Storage',
  'Productivity & Docs',
  'CRM & Sales',
  'Security & Observability',
  'Communication',
  'Data & Analytics',
  'Dev & Code',
  'Custom / Build your own',
];

const ATTENTION_STATUSES = new Set([
  'auth_expiring',
  'rate_limited',
  'schema_drift',
  'needs_mapping',
  'auth_failed',
  'auth_expired',
  'provider_down',
]);

const matchesSearch = (item, query, fields) => {
  if (!query) return true;
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  const targets = (fields?.length ? fields : ['name', 'category']).map((f) =>
    String(item[f] ?? '').toLowerCase(),
  );
  return targets.some((value) => value.includes(needle));
};

/**
 * Single definition of "connected" — `getIntegrationsStats`'s `active_count`
 * (the tab badge, CT-4158) and `listIntegrations({ filters: { connected: true } })`
 * (the grouped Already Integrated view, CT-4160) must always agree, so both
 * read this instead of each re-deriving the check.
 */
const isConnected = (item) => item.connected === true;

/**
 * List connected integrations (paginated / filtered / searched).
 * TODO(CT-3904 / backend): replace with `POST /v1/workspace/integrations/list`.
 */
export const listIntegrations = (params = {}) => {
  const {
    page = 1,
    page_size = 50,
    order_by = 'name',
    order_direction = 'asc',
    filters = {},
    search = {},
  } = params;

  const filtered = mockIntegrations
    .filter((item) => matchesFilters(item, filters))
    .filter((item) => matchesSearch(item, search.query, search.fields));

  const sorted = [...filtered].sort((a, b) => {
    const av = a[order_by];
    const bv = b[order_by];
    let cmp;
    if (typeof av === 'number' && typeof bv === 'number') cmp = av - bv;
    else cmp = String(av ?? '').localeCompare(String(bv ?? ''));
    return order_direction === 'desc' ? -cmp : cmp;
  });

  const total = sorted.length;
  const start = (page - 1) * page_size;
  const items = sorted.slice(start, start + page_size);

  return Promise.resolve(
    envelope(items, {
      total,
      page,
      page_size,
      total_pages: Math.max(1, Math.ceil(total / page_size)),
    }),
  );
};

/**
 * Workspace-level integration stats for the home hero.
 * TODO(CT-3904 / backend): replace with `GET /v1/workspace/integrations/stats`.
 */
export const getIntegrationsStats = () => {
  const active = mockIntegrations.filter(isConnected);
  const needsAttention = mockIntegrations.filter((item) => ATTENTION_STATUSES.has(item.status));
  const instances = mockIntegrations.reduce((sum, item) => sum + (item.instances_count ?? 0), 0);
  const categories = new Set(mockIntegrations.map((item) => item.category));

  return Promise.resolve(
    envelope({
      active_count: active.length,
      needs_attention_count: needsAttention.length,
      instances_count: instances,
      categories_count: categories.size,
    }),
  );
};

/**
 * Items surfaced in the "Needs Your Attention" band, severity-ordered.
 * TODO(CT-3904 / backend): replace with `GET /v1/workspace/integrations/attention`.
 */
// ⚠️ DEMO ONLY — REMOVE WHEN THE BACKEND EXISTS.
// Artificial latency (ms) for the mock below. It exists purely so the CT-4159
// loading skeleton (`NeedsAttentionBandSkeleton`) can be seen/QA'd against mock
// data, which otherwise resolves instantly. NOT a product behavior: keep it at 0
// for normal use, set >0 (e.g. 3000) only to demo the skeleton, and delete this
// constant and its use once the real endpoint lands (TODO CT-3904 below).
const MOCK_ATTENTION_LATENCY_MS = 0;

export const listAttentionItems = () => {
  const items = mockIntegrations
    .filter((item) => ATTENTION_STATUSES.has(item.status))
    .map((item) => ({
      id: item.id,
      slug: item.slug,
      name: item.name,
      category: item.category,
      status: item.status,
      calls_per_day: item.calls_per_day,
      used_in_workflows: item.used_in_workflows,
      connected_by: item.connected_by,
    }));

  const payload = envelope(items, { total: items.length });

  // ⚠️ DEMO ONLY (see MOCK_ATTENTION_LATENCY_MS): artificial delay so the loading
  // skeleton is visible against mock data. Remove when the real endpoint lands.
  return MOCK_ATTENTION_LATENCY_MS > 0
    ? new Promise((resolve) => setTimeout(() => resolve(payload), MOCK_ATTENTION_LATENCY_MS))
    : Promise.resolve(payload);
};

export { CATEGORY_ORDER, ATTENTION_STATUSES };
